// src/pages/workflow/WorkflowSettingsPage.tsx
// Workflow service settings: import/export YAML, global preferences.

import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
} from '@react/components';

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export const WorkflowSettingsPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();

  const [yamlContent, setYamlContent] = useState('');
  const [validationResult, setValidationResult] = useState<{ valid: boolean; errors: string[] } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setYamlContent(ev.target?.result as string || '');
      setValidationResult(null);
    };
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    if (!yamlContent.trim()) return;
    setIsValidating(true);
    try {
      const result = await store.ValidatePipelineYAML?.({ yaml_content: yamlContent });
      setValidationResult({
        valid: !!result?.is_valid,
        errors: (result?.errors || []).map((e) => (e.path ? `${e.path}: ${e.message}` : e.message)),
      });
    } catch (err: any) {
      setValidationResult({ valid: false, errors: [err?.message || 'Validation failed'] });
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!yamlContent.trim()) return;
    setIsImporting(true);
    setImportSuccess(false);
    try {
      await store.ImportPipelineYAML?.({ yaml_content: yamlContent });
      setImportSuccess(true);
      setYamlContent('');
      setValidationResult(null);
      setTimeout(() => setImportSuccess(false), 5000);
    } catch {
      // Error handled by store
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('workflow.settings')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('workflow.settings')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Import YAML */}
      <Card>
        <CardHeader>
          <CardTitle>{t('workflow.importYaml')}</CardTitle>
          <CardDescription>{t('workflow.importYamlDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".yaml,.yml"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon className="h-4 w-4 mr-1.5" />
              {t('workflow.uploadFile')}
            </Button>
          </div>

          <Textarea
            label={t('workflow.yamlContent')}
            value={yamlContent}
            onChange={(e) => {
              setYamlContent(e.target.value);
              setValidationResult(null);
            }}
            rows={12}
            className="font-mono text-sm"
            placeholder={t('workflow.yamlPlaceholder')}
          />

          {/* Validation result */}
          {validationResult && (
            validationResult.valid ? (
              <Alert variant="success">
                <CheckIcon className="h-4 w-4 inline mr-2" />
                {t('workflow.validYaml')}
              </Alert>
            ) : (
              <Alert variant="error" title={t('workflow.invalidYaml')}>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {validationResult.errors.map((err, i) => (
                    <li key={i} className="text-sm">{err}</li>
                  ))}
                </ul>
              </Alert>
            )
          )}

          {importSuccess && (
            <Alert variant="success" dismissible onDismiss={() => setImportSuccess(false)}>
              {t('workflow.importSuccess')}
            </Alert>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleValidate}
            disabled={!yamlContent.trim() || isValidating}
            isLoading={isValidating}
          >
            {t('workflow.validate')}
          </Button>
          <Button
            onClick={handleImport}
            disabled={!yamlContent.trim() || isImporting}
            isLoading={isImporting}
          >
            <UploadIcon className="h-4 w-4 mr-1.5" />
            {t('workflow.import')}
          </Button>
        </CardFooter>
      </Card>

      {/* Quick links */}
      <Card>
        <CardHeader>
          <CardTitle>{t('workflow.quickLinks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/workflows/approvals" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.approvals.title')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.approvals.subtitle')}</p>
              </div>
            </Link>
            <Link to="/workflows/credentials" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.credentials')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.manageCredentials')}</p>
              </div>
            </Link>
            <Link to="/workflows/node-types" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="3" y="3" width="7" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="14" y="3" width="7" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="3" y="14" width="7" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="14" y="14" width="7" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.nodeTypes')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.browseNodeTypes')}</p>
              </div>
            </Link>
            <Link to="/workflows/templates" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="3" y1="9" x2="21" y2="9" strokeLinecap="round" />
                <line x1="9" y1="21" x2="9" y2="9" strokeLinecap="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.templates')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.browseTemplates')}</p>
              </div>
            </Link>
            <Link to="/workflows/prompt-templates" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M7 8h10M7 12h6m-9 8V6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H8l-4 3z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.promptTemplates')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.managePromptTemplates')}</p>
              </div>
            </Link>
            <Link to="/workflows/agent-tools" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.agentTools')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.manageAgentTools')}</p>
              </div>
            </Link>
            <Link to="/workflows/policies" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.policyAdmin.title')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.policyAdmin.subtitle')}</p>
              </div>
            </Link>
            <Link to="/workflows" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <circle cx="5" cy="6" r="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="19" cy="6" r="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="20" r="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 6h10M12 8v2M12 14v4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">{t('workflow.pipelines')}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.viewAllPipelines')}</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default WorkflowSettingsPage;
